import * as React from "react";
import { css } from "@emotion/core";
import { Icon } from "@blueprintjs/core";
import { ScrollView } from "components/common/";

export interface SortState {
  columnKey: string;
  ascending: boolean;
}

export interface TableColumnConfig {
  label: React.ReactNode;
  width: string;
  sortable?: boolean;
  sortKey?: string;
  index?: number;
}

interface TableHeaderProps {
  columns: TableColumnConfig[];
  onSort?: (columnKey: string, ascending: boolean) => void;
  currentSort?: SortState;
}

export const TableHeader: React.FC<TableHeaderProps> = ({
  columns,
  onSort,
  currentSort,
}) => {
  return (
    <div
      css={css`
        display: flex;
        background: var(--neutral-6-translucent);
        border-bottom: 1px solid var(--neutral-6);
        padding: 0.5rem 1rem;
      `}
    >
      {columns.map((column) => {
        const isSorted =
          currentSort && currentSort.columnKey === column.sortKey;
        const isSortable = column.sortable && onSort;

        return (
          <div
            key={column.index}
            css={css`
              ${column.width ? `width: ${column.width};` : "flex: 1;"}
              font-weight: 700;
              font-size: 0.9rem;
              padding: 0 0.5rem;
              display: flex;
              align-items: center;
              gap: 0.5rem;
              ${isSortable ? "cursor: pointer;" : ""}
            `}
            onClick={() => {
              if (isSortable) {
                const newAscending =
                  isSorted && currentSort?.ascending ? false : true;
                onSort?.(column.sortKey || "", newAscending);
              }
            }}
          >
            <span>{column.label}</span>
            {isSorted && (
              <Icon
                icon={currentSort?.ascending ? "chevron-up" : "chevron-down"}
                size={14}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

interface TableBodyProps {
  rows: Array<{ id: string | number }>;
  columns: TableColumnConfig[];
  renderer: (rowIndex: number, columnIndex: number) => React.ReactNode;
  onRowClick: (rowIndex: number) => void;
}

const TableBody: React.FC<TableBodyProps> = ({
  rows,
  columns,
  renderer,
  onRowClick,
}) => {
  return (
    <ScrollView>
      <ul>
        {rows.map((row, rowIndex) => (
          <li
            key={row.id}
            css={css`
              display: flex;
              border-bottom: 1px solid var(--neutral-6);
              height: 42px;
              &:hover {
                background-color: #137cbd;
                cursor: pointer;
              }

              & > * {
                cursor: pointer;
              }

              padding: 0.5rem 1rem;
            `}
            onClick={() => onRowClick(rowIndex)}
          >
            {columns.map((column, columnIndex) => (
              <div
                key={column.index}
                css={css`
                  width: ${column.width};
                  padding: 0 0.5rem;
                  line-height: 1rem;
                  display: flex;
                  align-items: center;
                `}
              >
                {renderer(rowIndex, columnIndex)}
              </div>
            ))}
          </li>
        ))}
      </ul>
    </ScrollView>
  );
};

interface TableColumnProps {
  label: React.ReactNode;
  width: string;
  sortable?: boolean;
  sortKey?: string;
}

export const TableColumn: React.FC<TableColumnProps> = () => {
  return null;
};

interface TableProps {
  children: React.ReactElement<TableColumnProps>[];
  rows: Array<{ id: string | number }>;
  renderer: (rowIndex: number, columnIndex: number) => React.ReactNode;
  onRowClick: (rowIndex: number) => void;
  onSort?: (columnKey: string, ascending: boolean) => void;
  currentSort?: SortState;
}

export const Table: React.FC<TableProps> = ({
  children,
  rows,
  renderer,
  onRowClick,
  onSort,
  currentSort,
}) => {
  const columns = React.Children.map(children, (child, index) => ({
    ...child.props,
    index,
  }));

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        flex: 1;
        overflow: hidden;
      `}
    >
      <TableHeader
        columns={columns}
        onSort={onSort}
        currentSort={currentSort}
      />
      <TableBody
        rows={rows}
        columns={columns}
        renderer={renderer}
        onRowClick={onRowClick}
      />
    </div>
  );
};
